import dayjs from 'dayjs';
import React from 'react';
import ReactDOM from 'react-dom';
import timezone from 'dayjs/plugin/timezone';

import App from './App';
import reportWebVitals from './reportWebVitals';

import { barberProfilesReducer, initialState as barberProfiles } from './reducers/barberProfilesReducer';
import { reviewSignUpBoolReducer, initialState as SigUpModalReviewState } from './reducers/reviewModalAuthReducer';
import { BarberProfilesProvider } from './context/barberProfiles';
import { accountDataReducer, initialState as accountData } from './reducers/accountDataReducer';
import { AccountDataProvider } from './context/accountData';
import { countriesReducer, initialState as countries } from './reducers/countriesReducer';
import { CountriesProvider } from './context/countries';
import LazyLoadSentry from 'lazyLoadSentry';

import { SignUpReviewModalProvider } from 'context/reviewModalAuth';

import './styles/style.scss';

dayjs.extend(timezone);
dayjs.tz.setDefault('Etc/UTC');

// The booking screens host Stripe Elements, which report every uncaught error
// (including the browser's "ResizeObserver loop …" warning) to r.stripe.com/b.
// `react-spring-bottom-sheet` can put ResizeObserver into a loop that fires
// that warning every frame, so Stripe beacons it every frame until Chrome runs
// out of sockets (ERR_INSUFFICIENT_RESOURCES). We stop it three ways:
//
//   1. Defer ResizeObserver callbacks to the next animation frame, so a resize
//      can't synchronously trigger another resize in the same frame. This kills
//      the loop at its source for native-ResizeObserver consumers (Stripe etc).
//   2. Drop the synthetic error `@juggle/resize-observer` (bundled by the sheet)
//      dispatches via window.dispatchEvent.
//   3. Swallow any residual native error event in the capture phase, before it
//      reaches Stripe's / the dev overlay's listeners.
const IGNORED_RESIZE_OBSERVER_ERRORS = ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications'];
const isIgnoredResizeObserverError = (message) => typeof message === 'string' && IGNORED_RESIZE_OBSERVER_ERRORS.some((ignored) => message.includes(ignored));

if (!window.__roLoopPatched) {
  if (typeof window.ResizeObserver === 'function') {
    const NativeResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends NativeResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || entries.length) callback(entries, observer);
          });
        });
      }
    };
  }

  const nativeDispatchEvent = window.dispatchEvent.bind(window);
  window.dispatchEvent = (event) => {
    if (event && event.type === 'error' && isIgnoredResizeObserverError(event.message)) {
      return true;
    }
    return nativeDispatchEvent(event);
  };

  window.addEventListener(
    'error',
    (event) => {
      if (isIgnoredResizeObserverError(event?.message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );

  window.__roLoopPatched = true;
}

ReactDOM.render(
  <React.StrictMode>
    <LazyLoadSentry>
      <CountriesProvider initialState={countries} reducer={countriesReducer}>
        <AccountDataProvider initialState={accountData} reducer={accountDataReducer}>
          <BarberProfilesProvider initialState={barberProfiles} reducer={barberProfilesReducer}>
            <SignUpReviewModalProvider initialState={SigUpModalReviewState} reducer={reviewSignUpBoolReducer}>
              <App />
            </SignUpReviewModalProvider>
          </BarberProfilesProvider>
        </AccountDataProvider>
      </CountriesProvider>
    </LazyLoadSentry>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
